import { useContentStore } from "../../stores/content";
import { ComponentObject, CrossLayerConnection, PayloadLayer } from "../typeDefinations";
import ModuleTemplate from "../modules/moduleTemplate";
import { PayloadChannel, Point, PayloadCircle, PayloadPolygon, PayloadShape, PayloadChannelWithHill, PayloadLine, PayloadCurve } from "@/library/payloadPrimitives";
import { Channel } from "@/library/channel";
import { LevelConnection } from "@/library/levelConnection";
import { InletOutlet } from "@/library/inletOutlet";
import { Valve } from "@/library/valve";
import { Bridge } from "@/library/bridge";
import ComponentPolygon from "@/library/componentPolygon";
import ComponentCircle from "@/library/componentCircle";
import { ChannelPoint} from "../other";
import { buildChannelPayload } from "./channelPayloadBuilder";
import { buildPayloadPolygonPrimitive } from "../primitives";

const buildWall = (layerName: string, wallHeight?: number) => {
  const contentStore = useContentStore();
  const height = wallHeight ? wallHeight : contentStore.wallHeight;
  const channels = Array<PayloadChannelWithHill>();
  const channelPoints = [
    [[0, contentStore.wallThickness / 2], [contentStore.chipLengthX, contentStore.wallThickness / 2]],
    [[contentStore.chipLengthX - contentStore.wallThickness / 2, contentStore.wallThickness / 2], [contentStore.chipLengthX - contentStore.wallThickness / 2, contentStore.chipLengthY]],
    [[contentStore.chipLengthX, contentStore.chipLengthY - contentStore.wallThickness / 2], [0, contentStore.chipLengthY - contentStore.wallThickness / 2]],
    [[contentStore.wallThickness / 2, contentStore.chipLengthY - contentStore.wallThickness / 2], [contentStore.wallThickness / 2, contentStore.wallThickness / 2]]
  ];
  for (let i = 0; i < 4; i++) {
    const channel = new Channel(contentStore.wallThickness, height, layerName, 0, undefined, true);
    channel.channelDef.push(new ChannelPoint(channelPoints[i][0]));
    channel.channelDef.push(new ChannelPoint(channelPoints[i][1]));
    const wall = buildChannelPayload(channel, contentStore.chipThickness + height / 2, height);
    channels.push(wall);
  }
  return channels;
}

const createCornerComponent = (cornerId: string, points: number[][], circleCenter: Point, wallHeight?: number): ComponentObject[] => {
  const contentStore = useContentStore();
  const height = wallHeight ? wallHeight : contentStore.wallHeight;
  const compPolygon: ComponentObject = {
    id: `Polygon- ${cornerId}`,
    shapes: Array<PayloadShape>(),
    ports: Array<string>(),
    channels: Array<PayloadChannel>()
  };
  const shapes = buildPayloadPolygonPrimitive(
    points,
    false,
    0,
    contentStore.chipThickness,
    height,
    [0, 0],
    0,
    [0, 0]
  );
  if (shapes) compPolygon.shapes = shapes;

  const compCircle: ComponentObject = {
    id: `Circle-${cornerId}`,
    shapes: Array<PayloadShape>(),
    ports: Array<string>(),
    channels: Array<PayloadChannel>()
  };
  const circle = new PayloadCircle(
    circleCenter,
    false,
    height,
    contentStore.wallInnerCornerRadius
  );
  compCircle.shapes.push(circle);

  return [compPolygon, compCircle];
};

const createCorners = (wallHeight?: number) => {
  const contentStore = useContentStore();
  const corners = [
    {
      id: 'upperLeft',
      points: [
        [contentStore.wallThickness, contentStore.wallThickness], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness, contentStore.wallThickness + contentStore.wallInnerCornerRadius]
      ],
      circleCenter: new Point(contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'upperRight',
      points: [
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.wallThickness + contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius]
      ],
      circleCenter: new Point(contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'bottomRight',
      points: [
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness]
      ],
      circleCenter: new Point(contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'bottomLeft',
      points: [
        [contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness], 
        [contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness]
      ],
      circleCenter: new Point(contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    }
  ];
  const comp = Array<ComponentObject>();
  if (wallHeight && wallHeight !== 0) {
    corners.forEach(corner => {
      const [polygon, circle] = createCornerComponent(corner.id, corner.points, corner.circleCenter, wallHeight);
      comp.push(polygon, circle);
    });
  } else if (contentStore.wallHeight !== 0) {
    corners.forEach(corner => {
      const [polygon, circle] = createCornerComponent(corner.id, corner.points, corner.circleCenter);
      comp.push(polygon, circle);
    });
  } return comp;
}

const checkLevelConnection: (elm: LevelConnection | InletOutlet, layer: string) => boolean = (elm, layer) => {
  if (elm instanceof LevelConnection) {
    const startIndex = parseInt(elm.properties.endLv.value.split(' ')[1]);
    const endIndex = parseInt(elm.properties.startLv.value.split(' ')[1]);
    const lowerIndex = Math.min(startIndex, endIndex);
    const higherIndex = Math.max(startIndex, endIndex);
    if (parseInt(layer.split(' ')[1]) >= lowerIndex && parseInt(layer.split(' ')[1]) < higherIndex) {
      return true;
    } else return false;
  } else {
    if (elm.layers.layers.map(layerObject => layerObject.layer).includes(layer)) {
      return true;
    } else return false;
  }
}

// Function to determine if a curve shape represents a bridge (significant Z change)
function isBridgeCurve(curve: PayloadCurve): boolean {
  const { start, end, center } = curve;
  const d1 = Math.abs(start.z - end.z);
  const d2 = Math.abs(start.z - center.z);
  const d3 = Math.abs(end.z   - center.z);
  return Math.max(d1, d2, d3) > 1e-6;
}

export const buildChipJsonObject = (
  precision: string,
  globalWallHeight: boolean,
  wallHeights: number[],
  targetLayerIdx?: number
) => {
  const contentStore = useContentStore();
  const requestBody = {
    layers: Array<PayloadLayer>(),
    crosslayerConnections: Array<CrossLayerConnection>(),
    relation: [],
    general: {
      length: contentStore.chipLengthX,
      width: contentStore.chipLengthY,
      thickness: contentStore.chipThickness,
      precision: precision
    }
  };

  const layersArr = Array.from(contentStore.layers.values());
  layersArr.sort((l1, l2) => l1.height - l2.height);

  layersArr.forEach((layer, idx) => {
    if (targetLayerIdx !== undefined && idx !== targetLayerIdx) return;

    const wallComponents = Array<ComponentObject>();
    const components = Array<ComponentObject>();
    const channels = Array<PayloadChannel>();
    const valves = Array<ComponentObject>();
    const elevation = layer.height;

    // Use current logic from UI where wallHeight is determined by the global switch
    const currentWallHeight = globalWallHeight ? contentStore.chipProperties[6].value : wallHeights[idx];

    // build wall for STEP
    const layerName = layer.id;
    if (currentWallHeight && currentWallHeight !== 0) {
      const wallChannels = buildWall(layerName, currentWallHeight);
      channels.push(...(wallChannels as any));
    } else if (contentStore.wallHeight !== 0 && contentStore.wallThickness !== 0) {
      const wallChannels = buildWall(layerName);
      channels.push(...(wallChannels as any));
    }
    // wall inner corner radius
    if (contentStore.wallInnerCornerRadius !== 0) {
      const wallCorners = currentWallHeight ? createCorners(currentWallHeight): createCorners();
      wallCorners.forEach(corner => {
        wallComponents.push(corner);
      });
    }

    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm instanceof Channel) {
        elm.resetBridges();
        elm.resetValves();
      }
    });

    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm.display && elm instanceof Bridge) {
        elm.channelSegment.start.bridges.push(elm);
      } else if (elm.display && elm instanceof Valve) {
        elm.channelSegment.start.valves.push(elm);
      }
    });

    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm.display) {
        if (
          elm instanceof ModuleTemplate ||
          elm instanceof ComponentPolygon ||
          elm instanceof ComponentCircle
        ) {
          const p = elm.buildPayload(elevation, 0);
          if (p) components.push(p);
        } else if (elm instanceof Channel) {
          const payload = elm.buildPayload(elevation, 0);
          // For STEP, keep PayloadHill to ensure the bridge's positive solid envelope is exported
          const stepsShapes = payload.shapes as any;
          channels.push(new PayloadChannel(stepsShapes));
        } else if (elm instanceof Valve) {
          valves.push(elm.buildPayload(elevation));
        }
      }
    });

    // Add wallComponents and valves into main components
    components.push(...wallComponents);
    components.push(...valves);

    const newLayer: PayloadLayer = {
      elevation: elevation,
      components: components,
      channels: channels
    };
    requestBody.layers.push(newLayer);
  });

  // build level connection
  contentStore.levelConnectionIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm instanceof LevelConnection && elm.display) {
      if (targetLayerIdx !== undefined) {
        const targetLayerId = layersArr[targetLayerIdx].id;
        const startIndex = parseInt(elm.properties.endLv.value.split(' ')[1]);
        const endIndex = parseInt(elm.properties.startLv.value.split(' ')[1]);
        const lowerIndex = Math.min(startIndex, endIndex);
        const higherIndex = Math.max(startIndex, endIndex);
        const targetLayerNum = parseInt(targetLayerId.split(' ')[1]);
        if (!(targetLayerNum >= lowerIndex && targetLayerNum < higherIndex)) {
          return;
        }
      }

      const p1 = elm.buildPayload(() => 0, elm.properties.startLv.value);
      if (p1) {
        requestBody.crosslayerConnections.push(p1);
      }
    }
  });

  // build ports
  contentStore.portIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm instanceof InletOutlet && elm.display) {
      // Iterate over all layers the port is configured for, exactly like STL export does
      elm.layers.layers.forEach(layerObj => {
        if (targetLayerIdx !== undefined && layerObj.layer !== layersArr[targetLayerIdx].id) return;
        
        const targetLayerId = layerObj.layer;
        const targetLayer = contentStore.layers.get(targetLayerId);
        if (targetLayer) {
           const targetLayerIdxFound = layersArr.findIndex(l => l.id === targetLayerId);
           const currentWallHeight = globalWallHeight ? contentStore.chipProperties[6].value : wallHeights[targetLayerIdxFound];
           
           const p = elm.buildPayload(targetLayer.height, targetLayerId, currentWallHeight);
           if (p) {
             requestBody.crosslayerConnections.push(p);
           }
        }
      });
    }
  });

  // stringify then parse to cleanly drop undefined methods and ensure plain objects
  return JSON.parse(JSON.stringify(requestBody));
};
